/**
 * Enregistreur de message vocal — MediaRecorder + upload direct.
 * Compact et léger : pas de visualisation waveform, juste chrono + niveau simple.
 *
 * Formats : `audio/webm;codecs=opus` (Chrome/Edge/Firefox/Android),
 * fallback `audio/mp4` (Safari iOS). Le backend accepte tout `audio/*`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Send, X, Square } from 'lucide-react';

interface Props {
  onCancel: () => void;
  onSend: (blob: Blob, mime: string, dureeMs: number) => void;
  isDark?: boolean;
  maxDureeSecondes?: number;
}

function fmtDuree(ms: number): string {
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

function pickMime(): string {
  const candidats = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  const MR = (window as any).MediaRecorder;
  if (!MR) return '';
  for (const m of candidats) {
    try { if (MR.isTypeSupported(m)) return m; } catch { /* */ }
  }
  return '';
}

export const ChatVoiceRecorder: React.FC<Props> = ({
  onCancel, onSend, isDark = false, maxDureeSecondes = 180,
}) => {
  const [statut, setStatut] = useState<'demarrage' | 'enregistrement' | 'preview' | 'erreur'>('demarrage');
  const [dureeMs, setDureeMs] = useState(0);
  const [erreur, setErreur] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [mime, setMime] = useState<string>('');
  const recRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startRef = useRef<number>(0);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErreur('Micro non supporté par ce navigateur.');
        setStatut('erreur');
        return;
      }
      const mimeOk = pickMime();
      if (!mimeOk) {
        setErreur('Enregistrement audio non supporté.');
        setStatut('erreur');
        return;
      }
      setMime(mimeOk);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const rec = new (window as any).MediaRecorder(stream, { mimeType: mimeOk });
        recRef.current = rec;
        chunksRef.current = [];
        rec.ondataavailable = (e: any) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
          const b = new Blob(chunksRef.current, { type: mimeOk });
          setBlob(b);
          setPreviewUrl(URL.createObjectURL(b));
          setStatut('preview');
          if (timerRef.current) clearInterval(timerRef.current);
        };
        rec.start();
        startRef.current = Date.now();
        setStatut('enregistrement');
        timerRef.current = setInterval(() => {
          const d = Date.now() - startRef.current;
          setDureeMs(d);
          if (d / 1000 >= maxDureeSecondes) stop();
        }, 200);
      } catch (e: any) {
        setErreur(e?.name === 'NotAllowedError'
          ? 'Permission micro refusée.'
          : (e?.message || 'Impossible d\'accéder au micro.'));
        setStatut('erreur');
      }
    })();

    return () => {
      try { recRef.current?.stop?.(); } catch { /* */ }
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = () => {
    try { recRef.current?.stop?.(); } catch { /* */ }
    streamRef.current?.getTracks().forEach(t => t.stop());
  };

  const envoyer = () => {
    if (!blob) return;
    onSend(blob, mime || 'audio/webm', dureeMs);
  };

  if (statut === 'erreur') {
    return (
      <div className={`px-3 py-2 border-t flex items-center gap-2 text-xs ${
        isDark ? 'border-gray-700 bg-red-900/30 text-red-300' : 'border-gray-200 bg-red-50 text-red-700'
      }`}>
        <X size={14} className="text-red-500" />
        <span className="flex-1">{erreur}</span>
        <button onClick={onCancel} className="text-red-500 hover:text-red-700">Fermer</button>
      </div>
    );
  }

  return (
    <div className={`px-3 py-2 border-t flex items-center gap-2 ${
      isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-100'
    }`}>
      {statut === 'enregistrement' ? (
        <>
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
              <Mic size={14} className="text-white" />
            </div>
            <div className="absolute inset-0 rounded-full border-2 border-red-500 animate-ping opacity-50" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium text-red-500">Enregistrement…</div>
            <div className="text-[10px] text-gray-500">
              {fmtDuree(dureeMs)} / {fmtDuree(maxDureeSecondes * 1000)}
            </div>
          </div>
          <button onClick={onCancel}
            className="p-2 text-gray-500 hover:text-red-500" title="Annuler">
            <X size={16} />
          </button>
          <button onClick={stop}
            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full" title="Arrêter">
            <Square size={14} />
          </button>
        </>
      ) : statut === 'preview' && previewUrl ? (
        <>
          <audio src={previewUrl} controls className="flex-1 h-9" />
          <div className="text-[10px] text-gray-500 tabular-nums whitespace-nowrap">
            {fmtDuree(dureeMs)}
          </div>
          <button onClick={onCancel}
            className="p-2 text-gray-500 hover:text-red-500" title="Supprimer">
            <X size={16} />
          </button>
          <button onClick={envoyer}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full" title="Envoyer">
            <Send size={14} />
          </button>
        </>
      ) : (
        <div className="text-xs text-gray-500">Initialisation micro…</div>
      )}
    </div>
  );
};

export default ChatVoiceRecorder;
